<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title" class="mc-page-three" />
      <p class="mb-4">{{ $t('maintenance.order.desc') }}</p>

      <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
        <div class="float-end d-flex">
          <McButton
            variant="secondary"
            class="mx-2 mb-2 ms-auto"
            :text="$t('global.back')"
            icon="icon-outline_arrow-left"
            @click="
              $router.push({
                name: 'Service',
              })
            "
          />
          <McButton
            variant="primary"
            class="mb-2 ms-auto"
            :text="$t('global.addField')"
            icon="icon-fill_plus"
            @click="
              $router.push({
                name: 'Maintenance Create Service Order',
              })
            "
          />
        </div>
      </div>
    </div>

    <div v-if="render">
      <McGrapqlTable
        :apiClient="query"
        :fields="fields"
        :customHandlers="customHandlers"
        :hasCheckbox="true"
        :hasDelete="false"
        :searchable="true"
        :withCard="true"
        trackBy="serviceOrderId"
        :isSelectableField="false"
        :stickyHeader="true"
        :stickyCheckbox="true"
        :activeFilters="activeFilters"
        :hasDisabled="true"
        :hasSelectAll="false"
        :customButton="true"
        :hasFilter="true"
      >
        <template #button-filter="{ isSelected, showDeleteModal }">
          <div class="d-flex justify-content-between mb-3">
            <McInlineFilter
              :filterItems="filteredItems"
              :activeKeys="activeFilters['status']"
              @update:activeKeys="handleChangeActiveFilters([{ key: 'status', values: $event }])"
            />

            <ModalFilter
              :activeFilters="activeFilters"
              @handleChangeActiveFilters="handleChangeActiveFilters"
              :isSelected="isSelected"
              :showDeleteModal="showDeleteModal"
            />
          </div>
        </template>

        <template #viewManager="{ handleSetDataFields, dataFields, initialFields }">
          <McFilterSelect
            :defaultColumnOrder="initialFields"
            :currentColumnOrder="dataFields"
            @setColumn="handleSetDataFields"
            @setField="handleFieldShow"
          />
        </template>

        <template #cell(status)="data">
          <span :class="`mc-status mc-status--${data.value.color}`">
            {{ data.value.label }}
          </span>
        </template>

        <template #cell(geofence.fenceName)="data">
          {{ data.value ? data.value : data.item.address ? data.item.address : '-' }}
        </template>

        <template #cell(startTime)="data">
          {{ data.value ? momentDMY_HI(data.value) + ' WIB' : '-' }}
        </template>
        <template #cell(estimatedEndTime)="data">
          {{ data.value ? momentDMY_HI(data.value) + ' WIB' : '-' }}
        </template>
        <template #cell(actualEndTime)="data">
          {{ data.value ? momentDMY_HI(data.value) + ' WIB' : '-' }}
        </template>
      </McGrapqlTable>
    </div>
  </Layout>
</template>

<script>
import Layout from '@/router/layouts/main'
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header'
import McButton from '@/components/McButton'
import McGrapqlTable from '@/components/McGrapqlTable'
import McFilterSelect from '@/components/McFilterSelect'
import McInlineFilter from '@/components/McInlineFilter'
import filteredItems from './ModalFilter/filteredItems'
import ModalFilter from './ModalFilter'
import { GET_SERVICE_ORDERS, DELETE_SERVICE_ORDER } from '@/services/api/maintenance/serviceorder'
import { Helper } from '@/mixin/helper'
import moment from 'moment'

moment.locale('id')

export default {
  page: {
    title: 'Servis',
    meta: [{ name: 'description', content: appConfig.description }],
  },

  components: {
    Layout,
    PageHeader,
    McButton,
    McGrapqlTable,
    McFilterSelect,
    McInlineFilter,
    ModalFilter,
  },

  mixins: [Helper],

  data() {
    return {
      filteredItems,
      filterState: {
        all: true,
        draft: false,
        confirmation: false,
        inProgress: false,
        validation: false,
        done: false,
      },
      customHandlers: {
        handleClickRow: (data) => {
          this.$router.push({
            name: 'Maintenance Detail Service Order',
            params: { id: data.serviceOrderId },
          })
        },
        handleDisabledRow: (data) => {
          if (data.status !== 'Draft') return true
        },
      },
      activeFilters: {},
      vehicleCategories: [],
      vehicleTypes: [],
      location: [{}],
      filter: [],
      render: true,
      fieldShow: JSON.parse(localStorage.getItem('serviceOrderFields')),
    }
  },

  computed: {
    title() {
      return this.$t('maintenance.order.title')
    },
    storedFields() {
      const getColorAndLabel = (val, color, label) => ({
        color: color,
        label: this.$t(`maintenance.order.filter.${label}`),
      })

      const getStatusFormatter = (val) => {
        switch (val) {
          case 'Finished':
            return getColorAndLabel(val, 'green', 'finished')
          case 'InProgress':
            return getColorAndLabel(val, 'cyan', 'inProgress')
          case 'Confirmed':
            return getColorAndLabel(val, 'yellow-medium', 'confirmation')
          case 'Verification':
            return getColorAndLabel(val, 'purple', 'verification')
          case 'Cancelled':
            return getColorAndLabel(val, 'red', 'cancelled')
          default:
            return getColorAndLabel(val, 'grey', 'draft')
        }
      }

      const getPriceFormatter = (val) => (val ? `Rp. ${this.thousandFormat(val)}` : '-')

      for (const field of this.fieldShow.column) {
        if (field.key === 'status') {
          field.formatter = getStatusFormatter
        }
        if (field.key === 'price') {
          field.formatter = getPriceFormatter
        }
      }

      return this.fieldShow.column
    },

    fields() {
      if (this.fieldShow) {
        return this.storedFields
      } else {
        return [
          {
            key: 'code',
            label: this.$t('maintenance.order.soNo'),
            isSortable: true,
            isRedirectable: true,
            thStyle: 'width: 170px',
            tdAttr: {
              style: 'left:49.5px;position:sticky;min-width:170px;max-width:170px;',
            },
            thAttr: {
              style: 'left:49.5px;position:sticky;min-width:170px;max-width:170px;z-index:1;',
            },
            isLocked: true,
            isSticky: true,
          },
          {
            key: 'status',
            label: 'Status',
            isSortable: true,
            tdAttr: {
              style: 'left:219.5px;position:sticky;min-width:150px;',
            },
            thAttr: {
              style: 'left:219.5px;position:sticky;min-width:150px;z-index:1;',
            },
            isLocked: true,
            isSticky: true,
            formatter: (val) => {
              if (val == 'Finished') {
                return { color: 'green', label: this.$t('maintenance.order.filter.finished') }
              } else if (val == 'InProgress') {
                return {
                  color: 'cyan',
                  label: this.$t('maintenance.order.filter.inProgress'),
                }
              } else if (val == 'Confirmed') {
                return {
                  color: 'yellow-medium',
                  label: this.$t('maintenance.order.filter.confirmation'),
                }
              } else if (val == 'Verification') {
                return {
                  color: 'purple',
                  label: this.$t('maintenance.order.filter.verification'),
                }
              } else if (val == 'Cancelled') {
                return { color: 'red', label: this.$t('maintenance.order.filter.cancelled') }
              } else {
                return { color: 'grey', label: this.$t('maintenance.order.filter.draft') }
              }
            },
          },

          {
            key: 'vehicle.licensePlate',
            label: this.$t('global.object.licensePlate'),
            isSortable: true,
            isLocked: false,
          },
          {
            key: 'vehicle.vehicleType.type',
            label: this.$t('global.field.vehicleType'),
            isSortable: false,
            isLocked: false,
          },
          {
            key: 'locationType',
            label: this.$t('maintenance.order.serviceLocationType'),
            isSortable: false,
            isLocked: false,
          },
          {
            key: 'geofence.fenceName',
            label: this.$t('maintenance.order.serviceLocation'),
            isSortable: false,
            isLocked: false,
          },
          {
            key: 'pic.fullname',
            label: this.$t('maintenance.order.form.pic'),
            isSortable: false,
            isLocked: false,
          },
          {
            key: 'startTime',
            label: this.$t('maintenance.order.startDate'),
            isSortable: true,
            isLocked: false,
          },
          {
            key: 'estimatedEndTime',
            label: this.$t('maintenance.order.form.etaTime'),
            isSortable: false,
            isLocked: false,
          },
          {
            key: 'actualEndTime',
            label: this.$t('maintenance.order.actualEndTime'),
            isSortable: false,
            isLocked: false,
          },
          {
            key: 'price',
            label: this.$t('maintenance.order.form.totalPrice') + ' (Rp)',
            isSortable: false,
            formatter: (val) => {
              return val ? 'Rp. ' + this.thousandFormat(val) : '-'
            },
            isLocked: false,
          },
        ]
      }
    },

    query() {
      return {
        get: async (filter) => {
          if (!filter.orderBy) filter.orderBy = JSON.stringify({ code: 'desc' })

          const allFilter = {
            ...filter,
            ...this.activeFilters,
            startDateRange: this.activeFilters.startDateRange
              ? {
                  start: moment(
                    this.activeFilters.startDateRange[0] + ' 00:00:00.000'
                  ).toISOString(),
                  end: moment(this.activeFilters.startDateRange[1] + ' 23:59:59.000').toISOString(),
                }
              : null,
            estimatedEndDateRange: this.activeFilters.estimatedEndDateRange
              ? {
                  start: moment(
                    this.activeFilters.estimatedEndDateRange[0] + ' 00:00:00.000'
                  ).toISOString(),
                  end: moment(
                    this.activeFilters.estimatedEndDateRange[1] + ' 23:59:59.000'
                  ).toISOString(),
                }
              : null,
            actualEndDateRange: this.activeFilters.actualEndDateRange
              ? {
                  start: moment(
                    this.activeFilters.actualEndDateRange[0] + ' 00:00:00.000'
                  ).toISOString(),
                  end: moment(
                    this.activeFilters.actualEndDateRange[1] + ' 23:59:59.000'
                  ).toISOString(),
                }
              : null,
          }

          const res = await this.$apollo.query({
            query: GET_SERVICE_ORDERS,
            fetchPolicy: 'no-cache',
            variables: {
              filter: allFilter,
            },
          })

          return res.data.serviceOrders.serviceOrders
        },
        delete: async (id) => {
          const res = await this.$apollo.mutate({
            mutation: DELETE_SERVICE_ORDER,
            fetchPolicy: 'no-cache',
            variables: {
              input: {
                serviceOrderId: id,
              },
            },
          })

          return res.data.deleteServiceOrder.isSuccessful
        },
      }
    },
  },

  watch: {
    '$i18n.locale': function () {
      this.render = false

      setTimeout(() => {
        this.render = true
      }, 500)
    },
  },

  methods: {
    updateFilter(param) {
      this.filterState[param] = !this.filterState[param]

      if (param !== 'all') {
        this.filterState.all = false
      }

      // Check if all selected
      let obj = { ...this.filterState }
      let objArr = []
      Object.keys(obj).forEach(function (key) {
        if (obj[key]) {
          objArr.push(key)
        }
      })
      let exceptAll = Object.keys(obj).filter((key) => key != 'all').length
      if (objArr.length === exceptAll || objArr.length === 0 || objArr[0] === 'all') {
        this.showAllAlerts()
      }
    },

    showAllAlerts() {
      let obj = { ...this.filterState }
      Object.keys(obj).forEach(function (key) {
        if (key !== 'all') {
          obj[key] = false
        } else {
          obj[key] = true
        }
      })
      this.filterState = obj
    },

    handleChangeActiveFilters(filters) {
      filters.forEach((filter) => {
        // to reset the filter values
        this.$delete(this.activeFilters, filter.key)

        // to prevent set the filter in activeFilters if values are null, undefined, or empty string
        if (
          filter.values === null ||
          filter.values === undefined ||
          filter.values === '' ||
          filter.values === false
        )
          return
        // to prevent set the filter in activeFilters if values are Array with length equal to 0
        if (Array.isArray(filter.values) && filter.values.length === 0) return

        // set the key and values in activeFilters
        this.$set(this.activeFilters, filter.key, filter.values)
      })

      this.filter = filters
    },

    handleFieldShow(fields) {
      localStorage.setItem('serviceOrderFields', JSON.stringify({ column: fields }))
    },
  },
}
</script>
